import { CommonInput } from '@src/global/components/common/CommonInput';
import React, { useCallback } from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from '@src/global/utils/styling';
import { DisabledInputFunctions } from '@global/utils/disabledInputFunctions';
import { formatPostalCodeOnChange } from '@src/pages/checkout/components/BillingInformation/helper';

interface IProps {
  inputName: string;
  inputType?: string;
  defaultValue?: string;
  placeholder?: string;
  inputId?: string;
  className?: string;
  label?: string;
  disabledInputFunctions?: DisabledInputFunctions;
  onChangeTransform?: (value: string) => string;
}

export const ActiveInputText: React.FC<IProps> = ({
  inputName,
  inputType,
  label,
  defaultValue,
  placeholder,
  inputId,
  className,
  disabledInputFunctions,
  onChangeTransform,
}) => {
  const { t } = useTranslation('', {
    keyPrefix: 'activeForm',
  });

  const { field, fieldState } = useController({
    name: inputName,
    defaultValue: defaultValue,
  });

  // Watch country field for auto-formatting zip codes
  const { field: countryField } = useController({
    name: 'country',
    defaultValue: '',
  });

  // Wrapper to apply transformation if provided, or auto-format zip codes
  const handleChange = useCallback(
    (e: any) => {
      const value = e?.target?.value ?? e;
      let transformedValue = value;

      // Apply custom transform if provided
      if (onChangeTransform) {
        transformedValue = onChangeTransform(value);
      }
      // Auto-format zip codes
      else if (inputName === 'zip' && countryField.value) {
        transformedValue = formatPostalCodeOnChange(value, countryField.value);
      }

      field.onChange(transformedValue);
    },
    [field, onChangeTransform, inputName, countryField.value]
  );

  return (
    <div className={cn('active-input-text', className)}>
      <CommonInput
        value={field.value || ''}
        label={label || t(`${inputName}Label`)}
        type={inputType}
        placeholder={placeholder}
        id={inputId}
        onChange={handleChange as any}
        onBlur={field.onBlur}
        isValid={field.value && !fieldState?.error?.message}
        error={!!fieldState?.error}
        errorMessage={fieldState.error?.message}
        disabledInputFunctions={disabledInputFunctions}
      />
    </div>
  );
};
